import { useEffect, useState, useRef, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { SOS_LOGO_DARK_URL } from "@/components/Logo";
import { imageSrc } from "@/pages/Products";
import { useAuthedImage } from "@/lib/useAuthedImage";
import { formatMoney } from "@/lib/format";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import * as htmlToImage from "html-to-image";
import { jsPDF } from "jspdf";
import {
  ArrowLeft,
  Download,
  Ship,
  X,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { pickFeatureIcons } from "@/lib/featureIcons";
import { getEmbeddedFontCss } from "@/lib/fontEmbed";

// Configure the PDF.js worker. Hosting the .mjs from the matching CDN version
// avoids needing a custom webpack config inside CRA.
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;


function buildParticulars(v) {
  if (v.principal_particulars && v.principal_particulars.length) {
    return v.principal_particulars
      .filter((s) => s.enabled !== false)
      .filter((s) => {
        // Section headings need only a label.
        if ((s.kind || "row") === "heading") return !!(s.label || "").trim();
        // Data rows must have label or value, and skip AI-placeholder
        // values so the flyer never shows "Unknown" / "TBC" / "—" / "N/A".
        if (!s.label && !s.value) return false;
        const val = (s.value || "").toString().trim().toLowerCase();
        return val && !["unknown", "tbc", "tba", "n/a", "na", "-", "—", "?"].includes(val);
      });
  }
  const auto = [];
  if (v.material) auto.push({ label: "MATERIAL", value: v.material });
  return auto;
}

const SOS_BLUE = "#1E9CD7"; // Brand blue from the SOS logo (used for vessel name + features band)
const SOS_NAVY = "#002543"; // Deep navy used across the navy spine (matches the prior "Principal Particulars" header bar)
const SOS_CHARCOAL = "#3A4047"; // Dark grey from the SOS logo (used for sub-category, body)
const SOS_GREY = "#5A6470"; // Muted body grey

export default function VesselFlyer() {
  const { id } = useParams();
  const [vessel, setVessel] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfModal, setPdfModal] = useState({
    open: false,
    data: null, // Uint8Array
    loading: false,
    numPages: 0,
    pageNumber: 1,
    fileName: "vessel_GA.pdf",
  });
  const pdfContainerRef = useRef(null);
  const flyerRef = useRef(null);
  const [pdfPageWidth, setPdfPageWidth] = useState(900);
  const [producing, setProducing] = useState(false);
  // Natural aspect ratio (w / h) of the GA preview image, learned on load.
  // Defaults to a typical A3-landscape GA (1.41) so the slot is sensibly
  // sized while the image streams in.
  const [gaAspect, setGaAspect] = useState(1.41);
  // Persisted hero render adjustments. Default to a sensibly-large baseline so
  // typical renders (which usually have lots of empty padding around the boat)
  // fill the hero corner instead of floating mid-frame.
  const heroZoom = vessel?.render_zoom ?? 1.50;
  const heroOffsetX = vessel?.render_offset_x ?? 0;
  const heroOffsetY = vessel?.render_offset_y ?? 0;
  // Load the hero render through axios so an expired JWT (which silently
  // breaks raw `<img src=?auth=...>` tags) triggers the refresh-and-retry
  // interceptor and we end up with a fresh blob URL the browser can paint.
  const { src: heroSrc, loading: heroLoading } = useAuthedImage(
    vessel?.render_path,
  );
  // Debounce-save the hero adjustments back to the vessel record.
  const heroSaveTimer = useRef(null);
  const updateHero = (patch) => {
    setVessel((v) => (v ? { ...v, ...patch } : v));
    if (heroSaveTimer.current) clearTimeout(heroSaveTimer.current);
    heroSaveTimer.current = setTimeout(async () => {
      try {
        if (!vessel?.id) return;
        const merged = { ...vessel, ...patch };
        // Strip server-managed / unrelated fields the PUT endpoint doesn't expect.
        const body = {
          name: merged.name,
          sub_category: merged.sub_category || "",
          client_id: merged.client_id || null,
          vessel_type: merged.vessel_type || "",
          material: merged.material || "",
          notes: merged.notes || "",
          price: merged.price ?? null,
          price_currency: merged.price_currency || "USD",
          tagline: merged.tagline || "",
          description: merged.description || "",
          render_path: merged.render_path || null,
          ga_path: merged.ga_path || null,
          ga_preview_path: merged.ga_preview_path || null,
          ga_rotation: merged.ga_rotation || 0,
          render_zoom: merged.render_zoom ?? 1.50,
          render_offset_x: merged.render_offset_x ?? 0,
          render_offset_y: merged.render_offset_y ?? 0,
          is_favourite: !!merged.is_favourite,
          key_features: merged.key_features || [],
          principal_particulars: merged.principal_particulars || [],
        };
        await api.put(`/vessels/${vessel.id}`, body);
      } catch {
        // Silent — the local state still reflects the user's choice.
      }
    }, 500);
  };
  const resetHero = () =>
    updateHero({ render_zoom: 1.50, render_offset_x: 0, render_offset_y: 0 });

  // Persistent download URL for the produced flyer PDF. We build it
  // straight from the in-memory bytes via `URL.createObjectURL` — much
  // faster than the previous round-trip to the backend (`/flyer/stage`),
  // which was adding 5-15s of "Preparing…" on slow connections.
  // The download button uses the `<a download="...">` attribute to force
  // a save dialog, which all evergreen browsers honour on same-origin
  // blob URLs. Full-GA PDFs (which need anti-download protection) still
  // route through the backend stager via the existing flow below.
  const [pdfDownloadUrl, setPdfDownloadUrl] = useState(null);

  useEffect(() => {
    const isFullGa = !(pdfModal.fileName || "").toLowerCase().includes("_flyer");
    // For the produced flyer PDF we use a local blob URL — instant and
    // entirely client-side, so "Preparing…" never gets stuck on a slow
    // upload to the staging endpoint.
    if (pdfModal.data && !isFullGa) {
      const blob = new Blob([pdfModal.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      setPdfDownloadUrl(url);
      return () => {
        URL.revokeObjectURL(url);
      };
    }
    // Full-GA flow (or modal closed) — stage on backend so the protected
    // viewer URL is the only way to reach the bytes.
    setPdfDownloadUrl(null);
    if (!pdfModal.data || !isFullGa) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const fd = new FormData();
        const blob = new Blob([pdfModal.data], { type: "application/pdf" });
        fd.append("file", blob, pdfModal.fileName);
        const { data } = await api.post(
          `/flyer/stage?filename=${encodeURIComponent(pdfModal.fileName)}`,
          fd,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        if (cancelled) return;
        const base = process.env.REACT_APP_BACKEND_URL || "";
        setPdfDownloadUrl(`${base}/api/flyer/download/${data.token}`);
      } catch {
        if (!cancelled) setPdfDownloadUrl(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pdfModal.data, pdfModal.fileName]);

  // Memoize the {data} object passed to react-pdf. We slice() to give the
  // PDF.js worker its own fresh copy of the bytes — the worker transfers
  // (detaches) the underlying ArrayBuffer, so we don't want it to invalidate
  // the buffer we keep around for the Download button.
  const pdfFileProp = useMemo(
    () => (pdfModal.data ? { data: pdfModal.data.slice() } : null),
    [pdfModal.data]
  );

  useEffect(() => {
    let active = true;
    // Use allSettled so a single failed call (e.g. /settings 403 for a
    // client) doesn't blank the whole flyer with a false "vessel not
    // found" — the vessel itself is the critical request; settings is
    // optional company-info decoration.
    Promise.allSettled([api.get(`/vessels/${id}`), api.get("/settings")])
      .then(([vRes, sRes]) => {
        if (!active) return;
        if (vRes.status === "fulfilled") setVessel(vRes.value.data);
        if (sRes.status === "fulfilled") setSettings(sRes.value.data);
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [id]);

  // Self-heal: if the vessel's GA preview equals its GA PDF path (i.e. the
  // preview PNG never generated at upload), kick off a background regen and
  // swap in the fresh preview as soon as it's ready. Ensures the flyer always
  // shows the correct GA image without manual intervention.
  useEffect(() => {
    if (!vessel?.id) return;
    const gp = vessel.ga_path;
    const pp = vessel.ga_preview_path;
    const isPdf = gp && /\.pdf(\?|$)/i.test(gp);
    const previewMissing = !pp || pp === gp;
    if (!isPdf || !previewMissing) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post(`/vessels/${vessel.id}/regenerate-ga-preview`);
        if (cancelled) return;
        setVessel((v) => (v ? { ...v, ga_preview_path: data.ga_preview_path } : v));
      } catch {
        // Silent — the UI will continue to show the missing-preview placeholder.
      }
    })();
    return () => { cancelled = true; };
  }, [vessel?.id, vessel?.ga_path, vessel?.ga_preview_path]);

  // Track the container width so the PDF page scales fluidly inside the modal.
  useEffect(() => {
    if (!pdfModal.open) return;
    const el = pdfContainerRef.current;
    if (!el) return;
    const update = () => setPdfPageWidth(Math.max(320, el.clientWidth - 32));
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [pdfModal.open]);

  // While the FULL-GA viewer is open, block keyboard shortcuts that would
  // save or print the asset (Ctrl/Cmd+P, Ctrl/Cmd+S). This is a deterrent,
  // not an absolute lock — OS-level screenshots cannot be prevented from a
  // webpage and the visible watermark below covers that residual risk.
  useEffect(() => {
    if (!pdfModal.open) return;
    const isFullGa = !(pdfModal.fileName || "").toLowerCase().includes("_flyer");
    if (!isFullGa) return;
    const block = (e) => {
      const k = (e.key || "").toLowerCase();
      if ((e.ctrlKey || e.metaKey) && (k === "p" || k === "s")) {
        e.preventDefault();
        e.stopPropagation();
        toast.message("This drawing is protected — contact us for distribution.");
      }
    };
    window.addEventListener("keydown", block, true);
    return () => window.removeEventListener("keydown", block, true);
  }, [pdfModal.open, pdfModal.fileName]);

  const produce = async () => {
    const el = flyerRef.current;
    if (!el || producing) return;
    setProducing(true);
    // Temporarily hide any interactive-only chrome inside the flyer (e.g. the
    // "Open full GA PDF" button) so the produced PDF matches the print layout.
    const noPrintNodes = Array.from(el.querySelectorAll(".no-print"));
    const prevDisplays = noPrintNodes.map((n) => n.style.display);
    noPrintNodes.forEach((n) => (n.style.display = "none"));
    // Force the flyer container to A4 portrait aspect during capture so every
    // flyer — short or long description — produces an identically-proportioned
    // PDF. We do this by setting the page's height to its width × A4 ratio
    // and letting the GA row absorb any extra vertical space via flex-grow.
    // Targets the GA row explicitly (data-flyer-row="ga") — using a generic
    // selector here previously matched the HEADER row and squished the
    // description.
    const prev = {
      height: el.style.height,
      minHeight: el.style.minHeight,
      maxHeight: el.style.maxHeight,
      display: el.style.display,
      flexDirection: el.style.flexDirection,
    };
    const A4_RATIO = 297 / 210;
    const pageW = el.offsetWidth;
    const pageH = Math.round(pageW * A4_RATIO);
    el.style.height = `${pageH}px`;
    el.style.minHeight = `${pageH}px`;
    el.style.maxHeight = `${pageH}px`;
    el.style.display = "flex";
    el.style.flexDirection = "column";
    const middleRow = el.querySelector('[data-flyer-row="ga"]');
    const prevMiddle = middleRow
      ? { flex: middleRow.style.flex, minHeight: middleRow.style.minHeight }
      : null;
    if (middleRow) {
      middleRow.style.flex = "1 1 auto";
      middleRow.style.minHeight = "0";
    }
    const fileName = `${(vessel?.name || "vessel").replace(/\s+/g, "_")}_flyer.pdf`;
    // Open the viewer modal in loading state inside the user-gesture frame so
    // the resulting Download click stays inside an allowed gesture later.
    setPdfModal({ open: true, data: null, loading: true, numPages: 0, pageNumber: 1, fileName });
    try {
      // Make sure all web fonts are fully loaded before snapshotting — html-to-image
      // needs metrics-correct fonts to compute the foreignObject layout.
      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }
      // Build a self-contained @font-face CSS string with base64-inlined
      // Bebas Neue / Manrope / IBM Plex Mono. The SVG foreignObject that
      // html-to-image emits is decoded as an image — at that point the
      // surrounding page's <link rel="stylesheet"> rules don't apply, and
      // external font URLs can't be fetched cross-origin. Without this,
      // the SVG falls back to the next family in font-family (sans-serif),
      // whose wider glyphs cause "24M INSHORE TRAWLER" to wrap to two
      // lines and the "WORK BOAT" sub-category to visually overlap the
      // second line. We swallow fetch failures so the PDF still produces
      // (with the previous fallback look) if Google Fonts is unreachable.
      let fontEmbedCSS = null;
      try {
        fontEmbedCSS = await getEmbeddedFontCss();
      } catch (fontErr) {
        console.warn("[VesselFlyer] Font embed failed, falling back:", fontErr);
      }
      // Also wait until every <img> inside the flyer has fully decoded.
      // The hero render + GA preview load via blob: URLs created by
      // useAuthedImage, and Chrome will sometimes report `complete=true`
      // before the bitmap is actually ready for canvas drawing — when
      // that happens html-to-image's foreignObject snapshot fails on
      // particularly large GAs (multi-view drawings on the 24M Trawler,
      // for example). decode() blocks until the pixels are usable, so
      // the snapshot below always sees a finished bitmap.
      const imgs = Array.from(el.querySelectorAll("img"));
      await Promise.all(
        imgs.map((img) => {
          if (!img.src) return Promise.resolve();
          // decode() rejects if the URL never finished loading — we
          // swallow that so a single dud image doesn't abort the whole
          // PDF. Browsers without decode() fall back to a 'load' wait.
          if (typeof img.decode === "function") {
            return img.decode().catch(() => undefined);
          }
          if (img.complete) return Promise.resolve();
          return new Promise((resolve) => {
            img.addEventListener("load", resolve, { once: true });
            img.addEventListener("error", resolve, { once: true });
          });
        })
      );
      // Render via html-to-image (SVG foreignObject) instead of html2canvas.
      // html2canvas was dropping inter-word spaces at sub-pixel boundaries
      // because it re-rasterises text on a <canvas>. html-to-image leaves
      // text inside a native foreignObject, so the browser's own text engine
      // does the layout — word/letter spacing is preserved 1:1 with the
      // on-screen flyer.
      //
      // `skipFonts: true` — DO NOT walk document.styleSheets to inline
      // @font-face declarations. Google Fonts is served cross-origin so
      // reading `.cssRules` from JS throws SecurityError and the entire
      // PDF generation fails with 'Could not produce PDF'. Because the
      // foreignObject inherits the page's already-rendered glyphs (we
      // `await document.fonts.ready` just above), the captured snapshot
      // still uses the correct Manrope / Bebas Neue / IBM Plex faces.
      const canvas = await htmlToImage.toCanvas(el, {
        pixelRatio: 2,
        backgroundColor: "#ffffff",
        // ``cacheBust`` appends a ``?ts=...`` query to every <img> src
        // before html-to-image re-fetches it. That works for HTTP URLs
        // but breaks ``blob:`` URLs (the ones useAuthedImage hands out
        // for the hero render + GA preview) — the modified blob URL
        // never resolves, the fetch rejects, and the whole capture
        // aborts with a generic 'Could not produce PDF'. The blob
        // cache already guarantees the freshest bytes are in memory,
        // so cache-busting buys nothing here.
        cacheBust: false,
        // If the font embed succeeded, pass the inlined CSS so the SVG
        // renders with real Bebas Neue / Manrope rather than the
        // sans-serif fallback. ``fontEmbedCSS`` short-circuits
        // html-to-image's own ``document.styleSheets`` walk (which
        // throws SecurityError on cross-origin Google Fonts), so we
        // only ask it to ``skipFonts`` if the embed wasn't available.
        ...(fontEmbedCSS ? { fontEmbedCSS } : { skipFonts: true }),
      });
      const imgData = canvas.toDataURL("image/jpeg", 0.92);
      // Produce a proper A4 portrait PDF so it prints onto A4 paper without
      // the printer adding centering margins to fit.
      const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
      const pageW = 210;
      const pageH = 297;
      const imgAspect = canvas.width / canvas.height;
      // Fit the captured flyer to A4 width, top-aligned. If the flyer is
      // taller than A4 when scaled to width, fall back to fitting by height
      // (centred horizontally).
      const drawW = pageW;
      const drawH = pageW / imgAspect;
      if (drawH <= pageH) {
        pdf.addImage(imgData, "JPEG", 0, 0, drawW, drawH);
      } else {
        const fitH = pageH;
        const fitW = pageH * imgAspect;
        pdf.addImage(imgData, "JPEG", (pageW - fitW) / 2, 0, fitW, fitH);
      }
      const buffer = pdf.output("arraybuffer");
      setPdfModal({
        open: true,
        data: new Uint8Array(buffer),
        loading: false,
        numPages: 0,
        pageNumber: 1,
        fileName,
      });
      toast.success("Flyer PDF ready — use Download in the viewer");
    } catch (e) {
      setPdfModal({ open: false, data: null, loading: false, numPages: 0, pageNumber: 1, fileName: "vessel_flyer.pdf" });
      // Surface the real cause to the browser console — the catch-all
      // toast loses the stack trace and we can't debug "Could not
      // produce PDF" without it (every html-to-image failure mode looks
      // identical from outside). Including the message in the toast
      // lets the user copy/paste a clue if the console isn't open.
      console.error("[VesselFlyer] PDF generation failed:", e);
      const msg = (e && (e.message || e.name)) || "";
      toast.error(
        msg ? `Could not produce PDF — ${msg.slice(0, 120)}` : "Could not produce PDF"
      );
    } finally {
      noPrintNodes.forEach((n, i) => (n.style.display = prevDisplays[i]));
      // Restore the flyer's natural inline styles so the on-screen preview
      // returns to its normal height after capture.
      el.style.height = prev.height;
      el.style.minHeight = prev.minHeight;
      el.style.maxHeight = prev.maxHeight;
      el.style.display = prev.display;
      el.style.flexDirection = prev.flexDirection;
      if (middleRow && prevMiddle) {
        middleRow.style.flex = prevMiddle.flex;
        middleRow.style.minHeight = prevMiddle.minHeight;
      }
      setProducing(false);
    }
  };

  const openGaPdf = async () => {
    if (!vessel?.ga_path) return;
    const fileName = `${(vessel.name || "vessel").replace(/\s+/g, "_")}_GA.pdf`;
    setPdfModal({ open: true, data: null, loading: true, numPages: 0, pageNumber: 1, fileName });
    try {
      const res = await api.get(`/files/${vessel.ga_path}`, { responseType: "arraybuffer" });
      setPdfModal({
        open: true,
        data: new Uint8Array(res.data),
        loading: false,
        numPages: 0,
        pageNumber: 1,
        fileName,
      });
    } catch (e) {
      setPdfModal({ open: false, data: null, loading: false, numPages: 0, pageNumber: 1, fileName });
      toast.error("Could not open GA PDF");
    }
  };

  const closePdfModal = () => {
    setPdfModal({ open: false, data: null, loading: false, numPages: 0, pageNumber: 1, fileName: "vessel_GA.pdf" });
  };

  if (loading) {
    return <div className="p-10 text-[#94A3B8]">Loading flyer…</div>;
  }
  if (!vessel) {
    return (
      <div className="min-h-screen bg-[#0b0e12] flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <Ship className="h-12 w-12 text-[#3A4047] mx-auto mb-5" />
          <h1 className="font-display text-2xl font-bold text-white mb-2">
            We couldn&apos;t load this vessel
          </h1>
          <p className="text-sm text-[#94A3B8] mb-6">
            This vessel may have been removed or isn&apos;t available right now.
            Please head back to the fleet and pick another model.
          </p>
          <Link
            to="/vessels"
            data-testid="vessel-not-found-back"
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#1E9CD7] hover:bg-[#1682B5] text-white rounded-md font-display tracking-wide text-sm transition-colors"
          >
            <ArrowLeft className="h-4 w-4" /> Back to fleet
          </Link>
        </div>
      </div>
    );
  }

  const particulars = buildParticulars(vessel);
  const features = (vessel.key_features || []).filter((f) => f.title || f.description);
  const company = settings || {};

  return (
    <div className="min-h-screen bg-[#0b0e12] print:bg-white">
      {/* Toolbar (hidden on print) */}
      <div className="no-print sticky top-0 z-20 bg-[#0b0e12] border-b border-[#242930] px-4 py-3 flex items-center justify-between gap-4 flex-wrap">
        <Link
          to="/vessels"
          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.25em] text-[#94A3B8] hover:text-white"
          data-testid="back-to-vessels"
        >
          <ArrowLeft className="h-3 w-3" /> Back to vessels
        </Link>
        {vessel.render_path && (
          <div className="flex items-center gap-4 text-[10px] uppercase tracking-[0.2em] text-[#94A3B8]" data-testid="hero-adjust-panel">
            <span className="text-white/70 font-display">Hero</span>
            <label className="flex items-center gap-2">
              <span>Zoom</span>
              <input
                type="range"
                min="50"
                max="400"
                step="5"
                value={Math.round(heroZoom * 100)}
                onChange={(e) => updateHero({ render_zoom: Number(e.target.value) / 100 })}
                className="w-40 accent-[#1E9CD7]"
                data-testid="hero-zoom"
              />
              <span className="w-8 text-right tabular-nums text-white/80">
                {Math.round(heroZoom * 100)}%
              </span>
            </label>
            <label className="flex items-center gap-2">
              <span>X</span>
              <input
                type="range"
                min="-60"
                max="60"
                step="1"
                value={Math.round(heroOffsetX)}
                onChange={(e) => updateHero({ render_offset_x: Number(e.target.value) })}
                className="w-24 accent-[#1E9CD7]"
                data-testid="hero-offset-x"
              />
              <span className="w-8 text-right tabular-nums text-white/80">
                {Math.round(heroOffsetX)}
              </span>
            </label>
            <label className="flex items-center gap-2">
              <span>Y</span>
              <input
                type="range"
                min="-60"
                max="60"
                step="1"
                value={Math.round(heroOffsetY)}
                onChange={(e) => updateHero({ render_offset_y: Number(e.target.value) })}
                className="w-24 accent-[#1E9CD7]"
                data-testid="hero-offset-y"
              />
              <span className="w-8 text-right tabular-nums text-white/80">
                {Math.round(heroOffsetY)}
              </span>
            </label>
            <button
              type="button"
              onClick={resetHero}
              className="text-[10px] uppercase tracking-[0.2em] text-[#94A3B8] hover:text-white"
              data-testid="hero-reset"
            >
              Reset
            </button>
          </div>
        )}
        <button
          onClick={produce}
          disabled={producing}
          className="inline-flex items-center gap-2 px-4 py-2 rounded bg-[#1E9CD7] hover:bg-[#1682B5] disabled:opacity-60 text-white text-sm font-display font-semibold"
          data-testid="produce-pdf-btn"
        >
          <Download className="h-4 w-4" /> {producing ? "Producing…" : "Produce PDF"}
        </button>
      </div>

      {/* Flyer page */}
      <div className="mx-auto max-w-[1000px] my-6 print:my-0 print:max-w-none">
        <div
          ref={flyerRef}
          className="bg-white text-black shadow-2xl print:shadow-none sos-flyer-page pb-10"
          style={{ fontFamily: '"Manrope", "IBM Plex Sans", sans-serif' }}
        >
          {/* HEADER: logo + name + render */}
          <div className="grid grid-cols-12 gap-6 px-10 pt-10 pb-8">
            <div className="col-span-7 flex flex-col">
              <img src={SOS_LOGO_DARK_URL} alt="SOS" className="h-20 w-auto self-start mb-6" />
              <h1
                className="uppercase tracking-tight leading-[0.9] mt-1.5"
                style={{
                  fontFamily: '"Bebas Neue", "Manrope", sans-serif',
                  fontWeight: 700,
                  fontSize: "70px",
                  color: "#004AAD",
                  letterSpacing: "0.02em",
                }}
                data-testid="flyer-vessel-name"
              >
                {vessel.name}
              </h1>
              {vessel.sub_category ? (
                <div
                  className="uppercase tracking-tight leading-[1]"
                  style={{
                    fontFamily: '"Bebas Neue", "Manrope", sans-serif',
                    fontWeight: 700,
                    fontSize: "51px",
                    color: "#000000",
                    letterSpacing: "0.02em",
                    // Negative top margin pulls "WORK BOAT" up tight under
                    // the main vessel name. Bebas Neue's line-box at
                    // leading-0.9 already leaves ~8-12px of empty space
                    // below the cap baseline, so removing that void
                    // gives the hero a single visually-bonded block.
                    marginTop: "-4px",
                  }}
                >
                  {vessel.sub_category}
                </div>
              ) : (
                // Sub-category placeholder — keeps the hero column's total
                // height stable so the "GENERAL ARRANGEMENT" section starts
                // at the same Y position whether or not the vessel has a
                // sub-category, giving every flyer in the catalogue a
                // uniform top-block height. The 47px figure matches the
                // rendered sub-category contribution: 51px font-size at
                // leading-1 (= 51px line-box) minus the -4px tight-up
                // margin used on the real element.
                <div style={{ height: "47px" }} aria-hidden="true" />
              )}
              <div
                className="uppercase leading-[1] mt-4"
                style={{
                  fontFamily: '"Bebas Neue", "Manrope", sans-serif',
                  fontWeight: 700,
                  fontSize: "18px",
                  color: "#004AAD",
                  letterSpacing: "0.08em",
                }}
                data-testid="flyer-strapline"
              >
                BUILT FOR PERFORMANCE. ENGINEERED TO ENDURE
              </div>
              {vessel.tagline && (
                <p
                  className="mt-5 italic font-semibold uppercase"
                  style={{ fontSize: "11px", letterSpacing: "0.12em", color: SOS_CHARCOAL }}
                >
                  {vessel.tagline}
                </p>
              )}
              {vessel.description && (
                <p
                  className="mt-3 leading-relaxed"
                  style={{ fontSize: "13px", color: SOS_GREY, maxWidth: "100%" }}
                >
                  {vessel.description}
                </p>
              )}
            </div>
            <div className="col-span-5 flex items-center">
              <div
                className="aspect-[4/3] relative w-full"
                data-testid="flyer-render-wrap"
                style={{ overflow: "hidden" }}
              >
                {vessel.render_path && heroSrc ? (
                  <img
                    src={heroSrc}
                    alt={`${vessel.name} render`}
                    className="absolute inset-0 w-full h-full object-contain"
                    style={{
                      mixBlendMode: "multiply",
                      WebkitMaskImage:
                        "radial-gradient(ellipse 90% 82% at 50% 50%, black 35%, rgba(0,0,0,0.6) 65%, transparent 100%)",
                      maskImage:
                        "radial-gradient(ellipse 90% 82% at 50% 50%, black 35%, rgba(0,0,0,0.6) 65%, transparent 100%)",
                      WebkitPrintColorAdjust: "exact",
                      printColorAdjust: "exact",
                      transform: `translate(${heroOffsetX}%, ${heroOffsetY}%) scale(${heroZoom})`,
                      transformOrigin: "center center",
                    }}
                    data-testid="flyer-hero-render"
                  />
                ) : vessel.render_path && heroLoading ? (
                  <div className="absolute inset-0 flex items-center justify-center text-gray-300">
                    <Ship className="h-16 w-16 animate-pulse" />
                  </div>
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-gray-400" data-testid="flyer-hero-placeholder">
                    <Ship className="h-16 w-16" />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* FEATURES BAND (SOS bright blue) */}
          {features.length > 0 && (
            <div className="px-10 py-7" style={{ backgroundColor: "#004AAD" }}>
              <div className="grid grid-cols-5 gap-0">
                {(() => {
                  // Compute icons for ALL visible features in one pass so we
                  // can guarantee no duplicates across the row.
                  const visible = features.slice(0, 5);
                  const icons = pickFeatureIcons(visible);
                  return visible.map((kf, i) => {
                    const Icon = icons[i];
                    const isLast = i === visible.length - 1;
                    return (
                      <div
                        key={i}
                        className="px-3 text-center text-white"
                        style={
                          !isLast
                            ? { borderRight: "1px solid rgba(255,255,255,0.35)" }
                            : undefined
                        }
                      >
                        <div className="flex justify-center">
                          <div className="h-14 w-14 rounded-full bg-white flex items-center justify-center">
                            <Icon className="h-7 w-7" style={{ color: "#004AAD" }} strokeWidth={2.2} />
                          </div>
                        </div>
                        <div
                          className="mt-3 font-display font-extrabold uppercase tracking-wider"
                          style={{ fontSize: "11px" }}
                        >
                          {kf.title}
                        </div>
                        <div
                          className="mt-1 leading-snug"
                          style={{ fontSize: "10px", color: "rgba(255,255,255,0.92)" }}
                        >
                          {kf.description}
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          )}

          {/* GA + Particulars — left column stays white for the GA card; a
              flex-grow navy "filler" at the bottom of the left column absorbs
              any extra height so the bottom of the row reads cleanly into the
              navy bottom band (no white → navy step on the left side). */}
          <div className="grid grid-cols-12" data-flyer-row="ga">
            <div className="col-span-7 px-10 py-8 flex flex-col">
              <div
                className="py-2 font-display uppercase tracking-[0.2em] font-bold"
                style={{ fontSize: "11px", color: SOS_NAVY }}
              >
                General Arrangement
              </div>
              <div
                className="mt-2 bg-white border border-gray-300 relative overflow-hidden"
                style={{
                  // The GA panel can be WIDER than the original 4:3 reference
                  // (for landscape GAs), but never TALLER than it — so the
                  // panel never overruns the row on portrait-oriented
                  // multi-pane drawings. The drawing itself keeps its true
                  // proportions via `object-contain` inside the box.
                  aspectRatio: (() => {
                    const rot = ((vessel.ga_rotation || 0) % 360 + 360) % 360;
                    const sideways = rot === 90 || rot === 270;
                    const a = sideways ? 1 / gaAspect : gaAspect;
                    return Math.max(1.33, Math.min(2.2, a));
                  })(),
                }}
              >
                {vessel.ga_preview_path || vessel.ga_path ? (
                  (() => {
                    const rot = ((vessel.ga_rotation || 0) % 360 + 360) % 360;
                    const sideways = rot === 90 || rot === 270;
                    // Container now matches the image's natural aspect ratio
                    // (with rotation accounted for above), so for the un-rotated
                    // case the image simply fills the box. For rotated cases
                    // we still need a swapped wrap so the rotated visual fits.
                    const wrapStyle = sideways
                      ? {
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          // Container has aspect 1/gaAspect (i.e. tall),
                          // wrap fills it sideways: width = container height,
                          // height = container width.
                          width: `${(gaAspect * 100).toFixed(2)}%`,
                          height: `${(100 / gaAspect).toFixed(2)}%`,
                          transform: `translate(-50%, -50%) rotate(${rot}deg)`,
                          transformOrigin: "center center",
                        }
                      : {
                          position: "absolute",
                          inset: 0,
                          transform: rot ? `rotate(${rot}deg)` : undefined,
                          transformOrigin: "center center",
                        };
                    return (
                      <div style={wrapStyle}>
                        <img
                          src={imageSrc(vessel.ga_preview_path || vessel.ga_path)}
                          alt="General Arrangement"
                          onLoad={(e) => {
                            const w = e.currentTarget.naturalWidth;
                            const h = e.currentTarget.naturalHeight;
                            if (w > 0 && h > 0) setGaAspect(w / h);
                          }}
                          style={{ width: "100%", height: "100%", objectFit: "contain" }}
                        />
                      </div>
                    );
                  })()
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-gray-400 text-sm">
                    GA drawing not uploaded
                  </div>
                )}
              </div>
              {vessel.ga_path && /\.pdf(\?|$)/i.test(vessel.ga_path) && (
                <button
                  type="button"
                  onClick={openGaPdf}
                  className="no-print inline-flex items-center gap-1.5 mt-2 text-[11px] uppercase tracking-wider hover:underline"
                  style={{ color: SOS_BLUE }}
                  data-testid="open-ga-pdf"
                >
                  <Download className="h-3 w-3" /> Open full GA PDF
                </button>
              )}
              {/* Navy filler — flex-grows to fill any extra column height
                  when the right Principal Particulars column is taller than
                  the GA card. Negative margins cancel the col's px-10 py-8
                  so the filler bleeds edge-to-edge of the column. */}
              <div
                className="flex-1 -mx-10 -mb-8 mt-6"
                style={{ backgroundColor: SOS_NAVY, minHeight: 0 }}
                aria-hidden
              />
            </div>

            <div
              className="col-span-5 pl-6 pr-10 py-4"
              style={{ backgroundColor: SOS_NAVY }}
            >
              {/* Header bar — uses a thin white-translucent underline since the
                  whole navy panel now shares the same darkness. */}
              <div
                className="px-3 py-2 font-display font-bold uppercase text-white tracking-[0.2em]"
                style={{
                  fontSize: "11px",
                  borderBottom: "1px solid rgba(255,255,255,0.35)",
                }}
              >
                Principal Particulars
              </div>
              {particulars.length === 0 ? (
                <div className="text-sm text-white/70 mt-3 px-1">
                  Add particulars on the vessel form.
                </div>
              ) : (
                <dl className="mt-0">
                  {particulars.map((p, i) => {
                    if ((p.kind || "row") === "heading") {
                      // Section heading — visually matches the master
                      // "PRINCIPAL PARTICULARS" box above (left-aligned,
                      // same padding + tracking + underline) so the
                      // sections read as same-class siblings of the
                      // master title rather than odd inline labels.
                      return (
                        <div
                          key={i}
                          className="px-3 py-1.5 mt-2 font-display font-bold uppercase text-white tracking-[0.2em]"
                          style={{
                            fontSize: "11px",
                            borderBottom: "1px solid rgba(255,255,255,0.35)",
                          }}
                        >
                          {p.label}
                        </div>
                      );
                    }
                    return (
                      <div
                        key={i}
                        className="grid grid-cols-7 items-baseline gap-3 py-[3px] px-1"
                        style={{ borderBottom: "1px solid rgba(255,255,255,0.18)" }}
                      >
                        <dt
                          className="col-span-4 uppercase font-semibold"
                          style={{ fontSize: "10px", letterSpacing: "0.05em", color: "rgba(255,255,255,0.7)" }}
                        >
                          {p.label}
                        </dt>
                        <dd
                          className="col-span-3 font-display font-bold text-right"
                          style={{ fontSize: "12px", color: "#ffffff" }}
                        >
                          {p.value}
                        </dd>
                      </div>
                    );
                  })}
                </dl>
              )}
            </div>
          </div>

          {/* BOTTOM NAVY BAND */}
          <div
            className="px-10 pt-7 pb-14 flex items-center justify-between gap-6"
            style={{ backgroundColor: SOS_NAVY }}
          >
            <div className="min-w-0 max-w-[62%]">
              <div
                className="font-display font-extrabold uppercase tracking-tight text-white leading-tight whitespace-nowrap"
                style={{ fontSize: "22px" }}
              >
                Delivering Value. Driving the Future.
              </div>
              {(() => {
                const raw = (vessel.tagline || vessel.description || "").trim();
                if (!raw) return null;
                // Use Intl.Segmenter for proper sentence splitting that doesn't
                // break on decimals (e.g. "12.65 m"). Fall back to a basic
                // regex split for older browsers. Use ONLY the first sentence —
                // a width constraint below wraps it to ~3 even lines.
                let sentences;
                try {
                  const seg = new Intl.Segmenter("en", { granularity: "sentence" });
                  sentences = Array.from(seg.segment(raw)).map((s) => s.segment);
                } catch {
                  sentences = raw.match(/[^.!?]+[.!?]+(?=\s|$)|[^.!?]+$/g) || [raw];
                }
                const summary = (sentences.find((s) => s.trim()) || raw).trim();
                if (!summary) return null;
                return (
                  <div
                    className="mt-2 text-white/85 leading-snug"
                    style={{ fontSize: "11px", maxWidth: "360px" }}
                    data-testid="flyer-bottom-summary"
                  >
                    {summary}
                  </div>
                );
              })()}
            </div>
            <div className="text-white shrink-0">
              {/* Price block — uses simple block divs with lineHeight:1 and
                  paddingBottom (NOT margin / NOT <br> / NOT inline-block) so
                  html2canvas reproduces the exact spacing seen on screen. */}
              <div
                style={{
                  paddingLeft: "16px",
                  borderLeft: `4px solid ${SOS_BLUE}`,
                  textAlign: "left",
                }}
              >
                <div
                  className="font-display"
                  style={{
                    fontSize: "12px",
                    color: "rgba(255,255,255,0.85)",
                    fontWeight: 500,
                    letterSpacing: "0.18em",
                    textTransform: "uppercase",
                    lineHeight: 1,
                    paddingBottom: "10px",
                  }}
                >
                  From
                </div>
                <div
                  className="font-display tabular-nums"
                  style={{
                    fontSize: "38px",
                    color: SOS_BLUE,
                    letterSpacing: "-0.01em",
                    fontWeight: 800,
                    whiteSpace: "nowrap",
                    lineHeight: 1,
                  }}
                >
                  {vessel.price != null
                    ? `$${Number(vessel.price).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                    : "POA"}
                  {vessel.price != null && (
                    <span
                      style={{
                        fontSize: "14px",
                        marginLeft: "10px",
                        fontWeight: 500,
                        color: "rgba(255,255,255,0.85)",
                        letterSpacing: "0.08em",
                      }}
                    >
                      {vessel.price_currency || "USD"}
                    </span>
                  )}
                </div>
                {vessel.price != null && (
                  <div
                    style={{
                      fontSize: "10px",
                      color: "rgba(255,255,255,0.85)",
                      letterSpacing: "0.04em",
                      lineHeight: 1,
                      paddingTop: "10px",
                    }}
                    data-testid="flyer-price-disclaimer"
                  >
                    Price does not include specialist equipment like cranes, winches etc
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          @page { size: A4; margin: 14mm; }
          .no-print { display: none !important; }
          html, body { background: white !important; }
          /* Ensure the flyer renders without external shadows/borders inside the printable area */
          .sos-flyer-page {
            box-shadow: none !important;
            border: 0 !important;
            margin: 0 !important;
          }
        }
      `}</style>

      {/* GA PDF viewer modal */}
      {pdfModal.open && (() => {
        const isFullGa = !(pdfModal.fileName || "").toLowerCase().includes("_flyer");
        return (
        <div
          className="no-print fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          role="dialog"
          aria-modal="true"
          data-testid="ga-pdf-modal"
          onContextMenu={isFullGa ? (e) => e.preventDefault() : undefined}
          onDragStart={isFullGa ? (e) => e.preventDefault() : undefined}
        >
          <div className="bg-white rounded-md w-full max-w-5xl h-[90vh] flex flex-col shadow-2xl">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <div className="font-display font-bold uppercase tracking-wider text-sm text-[#0b0e12]">
                {vessel?.name} — {isFullGa ? "General Arrangement" : "Flyer"}
              </div>
              <div className="flex items-center gap-2">
                {!isFullGa && (
                  <a
                    href={pdfDownloadUrl || "#"}
                    download={pdfModal.fileName}
                    onClick={(e) => {
                      if (!pdfDownloadUrl) e.preventDefault();
                      else api.post("/activity/track", { type: "flyer_download", vessel_id: id }).catch(() => {});
                    }}
                    aria-disabled={!pdfDownloadUrl}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-white text-xs font-display font-semibold ${
                      pdfDownloadUrl
                        ? "bg-[#1E9CD7] hover:bg-[#1682B5]"
                        : "bg-[#1E9CD7]/50 cursor-not-allowed"
                    }`}
                    data-testid="modal-download-ga-pdf"
                    title="Saves the PDF to your downloads folder"
                  >
                    <Download className="h-3.5 w-3.5" /> {pdfDownloadUrl ? "Download" : "Preparing…"}
                  </a>
                )}
                <button
                  type="button"
                  onClick={closePdfModal}
                  className="inline-flex items-center justify-center h-8 w-8 rounded hover:bg-gray-100 text-[#3A4047]"
                  data-testid="modal-close-ga-pdf"
                  aria-label="Close"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div
              className="flex-1 bg-[#1f2126] relative overflow-auto"
              ref={pdfContainerRef}
              style={isFullGa ? { userSelect: "none", WebkitUserSelect: "none" } : undefined}
            >
              {pdfModal.loading || !pdfModal.data ? (
                <div className="absolute inset-0 flex items-center justify-center text-[#94A3B8] text-sm">
                  Loading GA PDF…
                </div>
              ) : (
                <div className="flex justify-center py-4 relative">
                  <Document
                    file={pdfFileProp}
                    onLoadSuccess={({ numPages }) =>
                      setPdfModal((m) => ({ ...m, numPages, pageNumber: 1 }))
                    }
                    onLoadError={() => toast.error("Failed to render GA PDF")}
                    loading={
                      <div className="text-[#94A3B8] text-sm py-10">Rendering page…</div>
                    }
                  >
                    <Page
                      pageNumber={pdfModal.pageNumber}
                      width={pdfPageWidth}
                      renderAnnotationLayer={false}
                      renderTextLayer={false}
                    />
                  </Document>
                  {/* Watermark overlay — visible on every screenshot copy of
                      this drawing. Diagonal tiling spans the whole page. */}
                  {isFullGa && (
                    <div
                      aria-hidden
                      className="pointer-events-none absolute inset-0 flex items-center justify-center overflow-hidden"
                      data-testid="ga-watermark"
                    >
                      <div
                        style={{
                          transform: "rotate(-30deg)",
                          color: "rgba(30, 156, 215, 0.18)",
                          fontFamily: '"Bebas Neue", "Manrope", sans-serif',
                          fontSize: "44px",
                          letterSpacing: "0.18em",
                          lineHeight: 2.6,
                          whiteSpace: "nowrap",
                          textAlign: "center",
                          textTransform: "uppercase",
                          fontWeight: 700,
                        }}
                      >
                        {Array.from({ length: 12 }).map((_, i) => (
                          <div key={i}>
                            Southern Ocean Solutions — Preliminary — Contact us
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {pdfModal.numPages > 1 && (
                <div className="sticky bottom-3 left-0 right-0 flex justify-center pointer-events-none">
                  <div className="pointer-events-auto inline-flex items-center gap-2 bg-black/70 backdrop-blur px-3 py-1.5 rounded-full text-white text-xs">
                    <button
                      type="button"
                      onClick={() =>
                        setPdfModal((m) => ({ ...m, pageNumber: Math.max(1, m.pageNumber - 1) }))
                      }
                      disabled={pdfModal.pageNumber <= 1}
                      className="disabled:opacity-40 inline-flex items-center justify-center h-6 w-6 rounded hover:bg-white/10"
                      data-testid="ga-pdf-prev-page"
                      aria-label="Previous page"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                    <span className="font-display tracking-wider">
                      {pdfModal.pageNumber} / {pdfModal.numPages}
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        setPdfModal((m) => ({
                          ...m,
                          pageNumber: Math.min(m.numPages, m.pageNumber + 1),
                        }))
                      }
                      disabled={pdfModal.pageNumber >= pdfModal.numPages}
                      className="disabled:opacity-40 inline-flex items-center justify-center h-6 w-6 rounded hover:bg-white/10"
                      data-testid="ga-pdf-next-page"
                      aria-label="Next page"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        );
      })()}
    </div>
  );
}
